// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// One load of a document for every view of it (enlarge-preview-annotate Req 10.1, 10.7; design "document loading").
// The Document_Viewer, the Page_Thumbnails, and the Enlarged_Viewer render pages from the value this hook returns,
// so opening, paging, and zooming the Enlarged_Viewer never fetch the document again. PDFs load with
// `disableRange: true`: pdf.js then fetches the file in one streamed request instead of range requests made later,
// when a page is first rendered. TIFFs are fetched once and decoded with UTIF; PNG and JPEG are shown from their URL
// (a second <img> of the same URL is served from the document's image list without a new request).
import { useEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import * as UTIF from 'utif';

import { pdfOptions } from '../lib/pdfjsWorker';

export type PdfProxy = Awaited<ReturnType<typeof pdfjs.getDocument>['promise']>;
export type DecodedTiff = { buf: ArrayBuffer; ifds: UTIF.IFD[] };

export type LoadedDocument =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'pdf'; pdf: PdfProxy; pageCount: number }
  | { kind: 'tiff'; tiff: DecodedTiff; pageCount: number }
  | { kind: 'image'; url: string; pageCount: 1 };

/** `getDocument` options for every viewer load. One frozen object, so a react-pdf consumer never sees a new identity. */
export const loadedPdfOptions = Object.freeze({ ...pdfOptions, disableRange: true });

function initial(url: string, contentType: string): LoadedDocument {
  return contentType === 'application/pdf' || contentType === 'image/tiff'
    ? { kind: 'loading' }
    : { kind: 'image', url, pageCount: 1 };
}

/**
 * Loads `url` once and returns its state. A new `url` or `contentType` starts a new load; the previous PDF proxy is
 * destroyed then, and on unmount.
 */
export function useLoadedDocument(url: string, contentType: string): LoadedDocument {
  const [state, setState] = useState<{ url: string; contentType: string; loaded: LoadedDocument }>(() => ({
    url,
    contentType,
    loaded: initial(url, contentType),
  }));
  // A new source starts a new load (state adjusted during render, not in an effect).
  if (state.url !== url || state.contentType !== contentType) {
    setState({ url, contentType, loaded: initial(url, contentType) });
  }

  useEffect(() => {
    let cancelled = false;
    const done = (loaded: LoadedDocument) => {
      if (!cancelled) setState({ url, contentType, loaded });
    };
    if (contentType === 'application/pdf') {
      const task = pdfjs.getDocument({ url, ...loadedPdfOptions });
      task.promise.then(
        (pdf) => done({ kind: 'pdf', pdf, pageCount: pdf.numPages }),
        () => done({ kind: 'error' }),
      );
      return () => {
        cancelled = true;
        // Destroying the loading task also destroys the document proxy it produced.
        void task.destroy();
      };
    }
    if (contentType === 'image/tiff') {
      const abort = new AbortController();
      fetch(url, { signal: abort.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.arrayBuffer();
        })
        .then((buf) => {
          const ifds = UTIF.decode(buf);
          if (ifds.length === 0) throw new Error('no TIFF pages');
          done({ kind: 'tiff', tiff: { buf, ifds }, pageCount: ifds.length });
        })
        .catch(() => done({ kind: 'error' }));
      return () => {
        cancelled = true;
        abort.abort();
      };
    }
    return () => {
      cancelled = true;
    };
  }, [url, contentType]);

  return state.loaded;
}
